import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { locationService } from '../services/api';
import { PlusIcon } from '../assets/icons';

const Locations = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [newLocation, setNewLocation] = useState({
    primary_name: '',
    external_location_code: '',
    meta_data: {
      i18n: {
        name: {
          'en_US': '',
          'zh_HK': '',
          'zh_CN': ''
        },
        address: {
          'en_US': '',
          'zh_HK': '',
          'zh_CN': ''
        }
      }
    }
  });
  const [editingLocation, setEditingLocation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load locations when component mounts
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoading(true);
        const data = await locationService.getLocations();
        setLocations(data.items || []);
      } catch (err) {
        console.error('Error loading locations:', err);
        setError(t('location.loadError', 'Failed to load locations. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, [t]);

  const handleNewLocationChange = (e) => {
    const { name, value } = e.target;
    setNewLocation(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewLocationI18nChange = (field, languageCode, value) => {
    setNewLocation(prev => ({
      ...prev,
      meta_data: {
        ...prev.meta_data,
        i18n: {
          ...prev.meta_data?.i18n,
          [field]: {
            ...prev.meta_data?.i18n?.[field],
            [languageCode]: value
          }
        }
      }
    }));
  };

  const handleAddLocation = async () => {
    // Validate required fields
    if (!newLocation.primary_name) {
      setError(t('location.fillRequired', 'Please fill in the primary name.'));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Build meta_data if any i18n names or addresses are provided
      const hasI18nNames = newLocation.meta_data?.i18n?.name &&
        Object.values(newLocation.meta_data.i18n.name).some(name => name && name.trim());
      const hasI18nAddresses = newLocation.meta_data?.i18n?.address &&
        Object.values(newLocation.meta_data.i18n.address).some(addr => addr && addr.trim());

      const locationData = {
        primary_name: newLocation.primary_name,
        external_location_code: newLocation.external_location_code || null
      };

      if (hasI18nNames || hasI18nAddresses) {
        locationData.meta_data = {
          i18n: {}
        };
        if (hasI18nNames) {
          locationData.meta_data.i18n.name = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.name).filter(([_, value]) => value && value.trim())
          );
        }
        if (hasI18nAddresses) {
          locationData.meta_data.i18n.address = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.address).filter(([_, value]) => value && value.trim())
          );
        }
      }

      const response = await locationService.addLocation(locationData);
      setLocations(prev => [...prev, response.data]);
      setNewLocation({
        primary_name: '',
        external_location_code: '',
        meta_data: {
          i18n: {
            name: {
              'en_US': '',
              'zh_HK': '',
              'zh_CN': ''
            },
            address: {
              'en_US': '',
              'zh_HK': '',
              'zh_CN': ''
            }
          }
        }
      });
      setShowAddLocationForm(false);
      setSuccessMessage(t('location.addSuccess', 'Location added successfully'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding location:', err);
      setError(err.response?.data?.detail || t('location.addError', 'Failed to add location. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setNewLocation({
      primary_name: location.primary_name || '',
      external_location_code: location.external_location_code || '',
      meta_data: location.meta_data || {
        i18n: {
          name: {
            'en_US': '',
            'zh_HK': '',
            'zh_CN': ''
          },
          address: {
            'en_US': '',
            'zh_HK': '',
            'zh_CN': ''
          }
        }
      }
    });
    setShowAddLocationForm(true);
  };

  const handleUpdateLocation = async () => {
    if (!editingLocation) return;

    // Validate required fields
    if (!newLocation.primary_name) {
      setError(t('location.fillRequired', 'Please fill in the primary name.'));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Build meta_data if any i18n names or addresses are provided
      const hasI18nNames = newLocation.meta_data?.i18n?.name &&
        Object.values(newLocation.meta_data.i18n.name).some(name => name && name.trim());
      const hasI18nAddresses = newLocation.meta_data?.i18n?.address &&
        Object.values(newLocation.meta_data.i18n.address).some(addr => addr && addr.trim());

      const locationData = {
        primary_name: newLocation.primary_name,
        external_location_code: newLocation.external_location_code || null
      };

      if (hasI18nNames || hasI18nAddresses) {
        locationData.meta_data = {
          i18n: {}
        };
        if (hasI18nNames) {
          locationData.meta_data.i18n.name = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.name).filter(([_, value]) => value && value.trim())
          );
        }
        if (hasI18nAddresses) {
          locationData.meta_data.i18n.address = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.address).filter(([_, value]) => value && value.trim())
          );
        }
      }

      const response = await locationService.updateLocation(editingLocation.id, locationData);
      setLocations(prev => prev.map(loc => loc.id === editingLocation.id ? response.data : loc));
      setEditingLocation(null);
      setNewLocation({
        primary_name: '',
        external_location_code: '',
        meta_data: {
          i18n: {
            name: {
              'en_US': '',
              'zh_HK': '',
              'zh_CN': ''
            },
            address: {
              'en_US': '',
              'zh_HK': '',
              'zh_CN': ''
            }
          }
        }
      });
      setShowAddLocationForm(false);
      setSuccessMessage(t('location.updateSuccess', 'Location updated successfully'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating location:', err);
      setError(err.response?.data?.detail || t('location.updateError', 'Failed to update location. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm(t('location.deleteConfirm', 'Are you sure you want to delete this location?'))) {
      return;
    }

    try {
      setLoading(true);
      await locationService.deleteLocation(locationId);
      setLocations(prev => prev.filter(loc => loc.id !== locationId));
      setSuccessMessage(t('location.deleteSuccess', 'Location deleted successfully'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting location:', err);
      setError(err.response?.data?.detail || t('location.deleteError', 'Failed to delete location. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddLocationForm(false);
    setEditingLocation(null);
    setNewLocation({
      primary_name: '',
      external_location_code: '',
      meta_data: {
        i18n: {
          name: {
            'en_US': '',
            'zh_HK': '',
            'zh_CN': ''
          },
          address: {
            'en_US': '',
            'zh_HK': '',
            'zh_CN': ''
          }
        }
      }
    });
    setError(null);
  };

  const getLanguageLabel = (code) => {
    const labels = {
      'en_US': 'English',
      'zh_HK': '繁體中文',
      'zh_CN': '简体中文'
    };
    return labels[code] || code;
  };

  if (loading && locations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">{t('location.loading', 'Loading locations...')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('location.title', 'Clinic Locations')}
            </h1>
          </div>
          {!showAddLocationForm && (
            <button
              onClick={() => setShowAddLocationForm(true)}
              className="px-4 py-2 bg-[#075e54] text-white rounded-md hover:bg-[#064e45] flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('location.addNew', 'Add New Location')}
            </button>
          )}
        </div>

        <div className="p-6">
          {successMessage && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          {showAddLocationForm && (
            <div className="mb-6 p-6 border rounded-lg bg-gray-50">
              <h4 className="text-lg font-medium mb-4">
                {editingLocation ? t('location.edit', 'Edit Location') : t('location.addNew', 'Add New Location')}
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('location.primaryName', 'Primary Name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="primary_name"
                    value={newLocation.primary_name}
                    onChange={handleNewLocationChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('location.externalCode', 'External Location Code')}
                  </label>
                  <input
                    type="text"
                    name="external_location_code"
                    value={newLocation.external_location_code}
                    onChange={handleNewLocationChange}
                    className="w-full p-2 border rounded"
                    placeholder={t('location.externalCodePlaceholder', 'Optional external code')}
                  />
                </div>

                <div className="border-t pt-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">
                    {t('location.i18nNames', 'Internationalized Names (Optional)')}
                  </h5>
                  {['en_US', 'zh_HK', 'zh_CN'].map(code => (
                    <div key={code} className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('location.name', 'Name')} ({getLanguageLabel(code)})
                      </label>
                      <input
                        type="text"
                        value={newLocation.meta_data?.i18n?.name?.[code] || ''}
                        onChange={(e) => handleNewLocationI18nChange('name', code, e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder={`${t('location.name', 'Name')} in ${getLanguageLabel(code)}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">
                    {t('location.i18nAddresses', 'Internationalized Addresses (Optional)')}
                  </h5>
                  {['en_US', 'zh_HK', 'zh_CN'].map(code => (
                    <div key={code} className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('location.address', 'Address')} ({getLanguageLabel(code)})
                      </label>
                      <textarea
                        value={newLocation.meta_data?.i18n?.address?.[code] || ''}
                        onChange={(e) => handleNewLocationI18nChange('address', code, e.target.value)}
                        className="w-full p-2 border rounded"
                        rows="2"
                        placeholder={`${t('location.address', 'Address')} in ${getLanguageLabel(code)}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={editingLocation ? handleUpdateLocation : handleAddLocation}
                    disabled={isSaving}
                    className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#064e45] disabled:opacity-50"
                  >
                    {isSaving ? t('common.saving', 'Saving...') : (editingLocation ? t('common.update', 'Update') : t('common.add', 'Add'))}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!showAddLocationForm && (
            <div>
              {locations.length === 0 ? (
                <p className="text-gray-500">{t('location.noLocations', 'No locations found. Add your first location to get started.')}</p>
              ) : (
                <div className="space-y-4">
                  {locations.map(location => (
                    <div key={location.id} className="border rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h5 className="font-medium text-gray-700">{location.primary_name}
                            {location.external_location_code && (
                              <span className="text-xs text-gray-500 ml-1">({location.external_location_code})</span>
                            )}
                          </h5>
                          {location.meta_data?.i18n?.name && Object.keys(location.meta_data.i18n.name).length > 0 && (
                            <div className="mt-2">
                              {Object.entries(location.meta_data.i18n.name).map(([lang, name]) => (
                                <div key={lang} className="text-xs text-gray-600 mr-2">
                                  <span className="font-medium">{getLanguageLabel(lang)}:</span> {name}
                                </div>
                              ))}
                            </div>
                          )}
                          {location.meta_data?.i18n?.address && Object.keys(location.meta_data.i18n.address).length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500">{t('location.localizedAddresses', 'Localized Addresses')}:</p>
                              {Object.entries(location.meta_data.i18n.address).map(([lang, address]) => (
                                <div key={lang} className="text-xs text-gray-600">
                                  {getLanguageLabel(lang)}: {address}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditLocation(location)}
                            className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            {t('common.edit', 'Edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteLocation(location.id)}
                            className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            {t('common.delete', 'Delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Locations;

